// ============================================================================
// FILE: validationHelpers.js
// Shared utility functions for all validation files
// ============================================================================

/**
 * Standard validation response format
 * @typedef {Object} ValidationResponse
 * @property {boolean} success - API call succeeded
 * @property {boolean} valid - Validation passed
 * @property {Array<ValidationError>} errors - Validation errors
 * @property {Array<ValidationWarning>} warnings - Validation warnings
 * @property {Object} metadata - Additional metadata
 */

/**
 * Standard error format
 * @typedef {Object} ValidationError
 * @property {string} type - Error type ('error' or 'critical')
 * @property {string} message - Error message
 * @property {string} [component] - Component instance ID
 * @property {string} [connection] - Connection ID
 * @property {string} [category] - Error category
 * @property {string} [solution] - Suggested solution
 */

/**
 * Standard warning format
 * @typedef {Object} ValidationWarning
 * @property {string} type - Warning type ('warning' or 'info')
 * @property {string} message - Warning message
 * @property {string} [component] - Component instance ID
 * @property {string} [category] - Warning category
 */

// ============================================================================
// RESPONSE PARSERS
// ============================================================================

/**
 * Parse backend validation response into standard format
 * @param {Object} backendResponse - Raw backend response
 * @returns {ValidationResponse}
 */
export function parseValidationResponse(backendResponse) {
  if (!backendResponse) {
    return createErrorResponse('No response from server');
  }

  // Handle error responses
  if (!backendResponse.success) {
    return {
      success: false,
      valid: false,
      errors: [{
        type: 'error',
        message: backendResponse.error || backendResponse.message || 'Validation failed',
        category: 'server_error'
      }],
      warnings: [],
      metadata: {}
    };
  }

  // Handle successful validation response
  const data = backendResponse.data || backendResponse;

  const allErrors = data.errors || data.validationErrors || [];

  return {
    success: true,
    valid: data.valid !== false,
    errors: normalizeErrors(allErrors.filter(e => e.type === 'error')),
    warnings: normalizeWarnings([
      ...allErrors.filter(e => e.type === 'warning'),
      ...(data.warnings || [])
    ]),
    metadata: {
      hasUnconnectedPins: data.hasUnconnectedPins,
      loopDelay: data.loopDelay,
      timestamp: new Date().toISOString(),
      ...data.metadata
    }
  };
  
  // return {
  //   success: true,
  //   valid: data.valid !== false, // Default to true if not specified
  //   errors: normalizeErrors(data.errors || data.validationErrors || []),
  //   warnings: normalizeWarnings(data.warnings || []),
  //   metadata: {
  //     hasUnconnectedPins: data.hasUnconnectedPins,
  //     loopDelay: data.loopDelay,
  //     timestamp: new Date().toISOString(),
  //     ...data.metadata
  //   }
  // };
}

/**
 * Normalize errors to standard format
 * @param {Array} errors - Raw errors from backend
 * @returns {Array<ValidationError>}
 */
function normalizeErrors(errors) {
  if (!Array.isArray(errors)) return [];
  
  return errors.map(error => {
    // If error is already in correct format
    if (typeof error === 'object' && error.message) {
      return {
        type: error.type || 'error',
        message: error.message,
        component: error.component,
        connection: error.connection,
        category: error.category || 'validation',
        solution: error.solution
      };
    }
    
    // If error is just a string
    if (typeof error === 'string') {
      return {
        type: 'error',
        message: error,
        category: 'validation'
      };
    }
    
    return {
      type: 'error',
      message: 'Unknown error',
      category: 'unknown'
    };
  });
}

/**
 * Normalize warnings to standard format
 * @param {Array} warnings - Raw warnings from backend
 * @returns {Array<ValidationWarning>}
 */
function normalizeWarnings(warnings) {
  if (!Array.isArray(warnings)) return [];
  
  return warnings.map(warning => {
    // If warning is already in correct format
    if (typeof warning === 'object' && warning.message) {
      return {
        type: warning.type || 'warning',
        message: warning.message,
        component: warning.component,
        category: warning.category || 'validation'
      };
    }
    
    // If warning is just a string
    if (typeof warning === 'string') {
      return {
        type: 'warning',
        message: warning,
        category: 'validation'
      };
    }
    
    return {
      type: 'warning',
      message: 'Unknown warning',
      category: 'unknown'
    };
  });
}

// ============================================================================
// CATEGORIZATION
// ============================================================================

/**
 * Categorize errors by type
 * @param {Array<ValidationError>} errors
 * @returns {Object} Categorized errors
 */
export function categorizeErrors(errors) {
  const categorized = {
    circuit: [],
    connection: [],
    configuration: [],
    code: [],
    other: []
  };

  errors.forEach(error => {
    const category = error.category || 'other';
    if (categorized[category]) {
      categorized[category].push(error);
    } else {
      categorized.other.push(error);
    }
  });

  return categorized;
}

/**
 * Categorize warnings by type
 * @param {Array<ValidationWarning>} warnings
 * @returns {Object} Categorized warnings
 */
export function categorizeWarnings(warnings) {
  const categorized = {
    circuit: [],
    connection: [],
    configuration: [],
    code: [],
    other: []
  };

  warnings.forEach(warning => {
    const category = warning.category || 'other';
    if (categorized[category]) {
      categorized[category].push(warning);
    } else {
      categorized.other.push(warning);
    }
  });

  return categorized;
}

// ============================================================================
// RESPONSE BUILDERS
// ============================================================================

/**
 * Create success response
 * @param {Array} warnings - Optional warnings
 * @returns {ValidationResponse}
 */
export function createSuccessResponse(warnings = []) {
  return {
    success: true,
    valid: true,
    errors: [],
    warnings: normalizeWarnings(warnings),
    metadata: {
      timestamp: new Date().toISOString()
    }
  };
}

/**
 * Create error response
 * @param {string|Array} errors - Error message(s)
 * @returns {ValidationResponse}
 */
export function createErrorResponse(errors) {
  const errorArray = Array.isArray(errors) ? errors : [errors];
  
  return {
    success: false,
    valid: false,
    errors: normalizeErrors(errorArray),
    warnings: [],
    metadata: {
      timestamp: new Date().toISOString()
    }
  };
}

/**
 * Create warning response (valid but with warnings)
 * @param {Array} warnings - Warning messages
 * @returns {ValidationResponse}
 */
export function createWarningResponse(warnings) {
  return {
    success: true,
    valid: true,
    errors: [],
    warnings: normalizeWarnings(warnings),
    metadata: {
      timestamp: new Date().toISOString()
    }
  };
}

// ============================================================================
// MERGING
// ============================================================================

/**
 * Merge multiple validation responses
 * @param {Array<ValidationResponse>} responses
 * @returns {ValidationResponse}
 */
export function mergeValidationResponses(responses) {
  const merged = {
    success: true,
    valid: true,
    errors: [],
    warnings: [],
    metadata: {}
  };

  responses.forEach(response => {
    if (!response) return;
    
    // If any response failed, merged fails
    if (!response.success) {
      merged.success = false;
    }
    
    // If any validation failed, merged validation fails
    if (!response.valid) {
      merged.valid = false;
    }
    
    // Merge errors
    if (response.errors && response.errors.length > 0) {
      merged.errors.push(...response.errors);
    }
    
    // Merge warnings
    if (response.warnings && response.warnings.length > 0) {
      merged.warnings.push(...response.warnings);
    }
    
    // Merge metadata
    if (response.metadata) {
      merged.metadata = { ...merged.metadata, ...response.metadata };
    }
  });

  return merged;
}

// ============================================================================
// FILTERING
// ============================================================================

/**
 * Filter errors by component
 * @param {Array<ValidationError>} errors
 * @param {string} componentId
 * @returns {Array<ValidationError>}
 */
export function filterErrorsByComponent(errors, componentId) {
  return errors.filter(error => error.component === componentId);
}

/**
 * Filter warnings by component
 * @param {Array<ValidationWarning>} warnings
 * @param {string} componentId
 * @returns {Array<ValidationWarning>}
 */
export function filterWarningsByComponent(warnings, componentId) {
  return warnings.filter(warning => warning.component === componentId);
}

/**
 * Get errors by category
 * @param {Array<ValidationError>} errors
 * @param {string} category
 * @returns {Array<ValidationError>}
 */
export function getErrorsByCategory(errors, category) {
  return errors.filter(error => error.category === category);
}

// ============================================================================
// FORMATTING
// ============================================================================

/**
 * Format validation results for display
 * @param {ValidationResponse} response
 * @returns {Object} Formatted for UI
 */
export function formatForDisplay(response) {
  return {
    isValid: response.valid,
    hasErrors: response.errors.length > 0,
    hasWarnings: response.warnings.length > 0,
    errorCount: response.errors.length,
    warningCount: response.warnings.length,
    categorizedErrors: categorizeErrors(response.errors),
    categorizedWarnings: categorizeWarnings(response.warnings),
    summary: generateSummary(response)
  };
}

/**
 * Generate validation summary message
 * @param {ValidationResponse} response
 * @returns {string}
 */
function generateSummary(response) {
  if (!response.success) {
    return 'Validation failed to complete';
  }
  
  if (response.valid && response.warnings.length === 0) {
    return 'All validations passed ✓';
  }
  
  if (response.valid && response.warnings.length > 0) {
    return `Validation passed with ${response.warnings.length} warning(s)`;
  }
  
  if (!response.valid) {
    return `Validation failed with ${response.errors.length} error(s)`;
  }
  
  return 'Validation complete';
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Handle API errors gracefully
 * @param {Error} error - API error
 * @param {string} context - Context of the error
 * @returns {ValidationResponse}
 */
export function handleApiError(error, context = 'API call') {
  console.error(`❌ ${context} failed:`, error);
  
  // Network error
  if (!error.response) {
    return createErrorResponse({
      type: 'error',
      message: 'Network error. Please check your connection.',
      category: 'network_error'
    });
  }
  
  // Server error
  if (error.response?.status >= 500) {
    return createErrorResponse({
      type: 'error',
      message: 'Server error. Please try again later.',
      category: 'server_error'
    });
  }
  
  // Client error
  if (error.response?.data?.message) {
    return createErrorResponse({
      type: 'error',
      message: error.response.data.message,
      category: 'validation_error'
    });
  }
  
  // Generic error
  return createErrorResponse({
    type: 'error',
    message: error.message || 'An unexpected error occurred',
    category: 'unknown_error'
  });
}

// ============================================================================
// VALIDATION STATE MANAGEMENT
// ============================================================================

/**
 * Check if validation is in progress
 * @param {Object} validationState
 * @returns {boolean}
 */
export function isValidating(validationState) {
  return validationState?.isValidating === true;
}

/**
 * Check if validation has errors
 * @param {ValidationResponse} response
 * @returns {boolean}
 */
export function hasErrors(response) {
  return response?.errors && response.errors.length > 0;
}

/**
 * Check if validation has warnings
 * @param {ValidationResponse} response
 * @returns {boolean}
 */
export function hasWarnings(response) {
  return response?.warnings && response.warnings.length > 0;
}

/**
 * Check if validation passed completely (no errors, no warnings)
 * @param {ValidationResponse} response
 * @returns {boolean}
 */
export function isClean(response) {
  return response?.valid && !hasErrors(response) && !hasWarnings(response);
}

// ============================================================================
// EXPORT ALL
// ============================================================================

export default {
  // Parsers
  parseValidationResponse,
  
  // Categorization
  categorizeErrors,
  categorizeWarnings,
  
  // Builders
  createSuccessResponse,
  createErrorResponse,
  createWarningResponse,
  
  // Merging
  mergeValidationResponses,
  
  // Filtering
  filterErrorsByComponent,
  filterWarningsByComponent,
  getErrorsByCategory,
  
  // Formatting
  formatForDisplay,
  
  // Error handling
  handleApiError,
  
  // State checks
  isValidating,
  hasErrors,
  hasWarnings,
  isClean
};